import { apiUrl } from './api';

/** 控制台 AI 客服的客户端：状态查询 + SSE 流式对话。 */

export interface AssistantStatus {
  surface: 'console';
  available: boolean;
  greeting: string;
  suggestedQuestions: string[];
}

export interface AssistantChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AssistantSource {
  id: string;
  title: string;
}

export class AssistantError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AssistantError';
  }
}

export async function fetchAssistantStatus(signal?: AbortSignal): Promise<AssistantStatus | null> {
  try {
    const response = await fetch(apiUrl('/api/assistant/status?surface=console'), {
      credentials: 'include',
      signal,
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { ok?: boolean; data?: AssistantStatus };
    if (body.ok !== true || !body.data) return null;
    return body.data;
  } catch {
    return null;
  }
}

export interface StreamHandlers {
  onSources?: (sources: AssistantSource[]) => void;
  onDelta: (text: string) => void;
  onDone?: () => void;
}

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: { message?: unknown } };
    if (typeof body.error?.message === 'string' && body.error.message !== '') {
      return body.error.message;
    }
  } catch {
    // 流式接口在出错时也可能返回非 JSON，统一退回通用文案。
  }
  if (response.status === 429) return '提问过于频繁，请稍后再试';
  if (response.status === 403) return 'AI 客服暂未开放';
  return 'AI 客服暂时不可用，请稍后再试';
}

/**
 * 发起流式对话。服务端协议见 CONSOLE/worker/src/lib/assistant.ts：
 * sources → delta... → done（错误时 error）。
 */
export async function streamAssistantChat(
  messages: AssistantChatMessage[],
  handlers: StreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(apiUrl('/api/assistant/chat'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ surface: 'console', messages }),
    signal,
  });

  if (!response.ok || !response.body) {
    throw new AssistantError(await readErrorMessage(response));
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary >= 0) {
        const chunk = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        handleChunk(chunk, handlers);
        boundary = buffer.indexOf('\n\n');
      }
    }
    if (buffer.trim() !== '') handleChunk(buffer, handlers);
  } finally {
    reader.releaseLock();
  }
}

function handleChunk(chunk: string, handlers: StreamHandlers): void {
  for (const line of chunk.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data:')) continue;
    const raw = trimmed.slice(5).trim();
    if (raw === '' || raw === '[DONE]') continue;

    let payload: unknown;
    try {
      payload = JSON.parse(raw);
    } catch {
      continue;
    }
    if (payload === null || typeof payload !== 'object') continue;
    const event = payload as Record<string, unknown>;

    if (event.type === 'delta' && typeof event.text === 'string') {
      handlers.onDelta(event.text);
    } else if (event.type === 'sources' && Array.isArray(event.sources)) {
      handlers.onSources?.(
        event.sources
          .filter((item): item is Record<string, unknown> => item !== null && typeof item === 'object')
          .map((item) => ({
            id: typeof item.id === 'string' ? item.id : '',
            title: typeof item.title === 'string' ? item.title : '',
          })),
      );
    } else if (event.type === 'done') {
      handlers.onDone?.();
    } else if (event.type === 'error') {
      throw new AssistantError(
        typeof event.message === 'string' ? event.message : '生成回答时发生错误',
      );
    }
  }
}
